import * as React from 'react'

import {
  BoxIcon,
  ChartColumnIcon,
  ComponentIcon,
  HouseIcon,
  MapPinIcon,
  PackageIcon,
  PaperclipIcon,
  RadioIcon,
  TruckIcon,
  WrenchIcon,
} from 'lucide-react'
import { observer } from 'mobx-react'

import { DefaultLandingPage } from '~/client/graph'
import { Separator } from '~/client/src/shared/components/shadcn-ui/separator'
import { GenericWorkflowIcon } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ProjectSettingsStore, { ResourceApp } from '../../ProjectSettings.store'
import { SettingsFormSection } from '../SettingsFormSection'
import AppToggle from './components/AppToggle'
import AppTogglesHeader from './components/AppTogglesHeader'

interface ProjectTypeSettingProps {
  store: ProjectSettingsStore
  isDeliveriesToggleAvailable: boolean
  isLogisticsToggleAvailable: boolean
  isMaterialsToggleAvailable: boolean
  isMaterialResourcesToggleAvailable: boolean
  isPhotosAvailable: boolean
  isFormsToggleAvailable: boolean
  isTrackerToggleAvailable: boolean
  isAnalyticsAvailable: boolean
  isEquipmentToggleAvailable: boolean
  isStruxTagsAvailable: boolean
  isLocationsToggleAvailable: boolean
}

interface AppConfig {
  isAvailable: boolean
  label: string
  isEnabled: boolean
  onToggle: () => void
  icon?: JSX.Element
  landingPage: DefaultLandingPage
  isActiveApp: boolean
  resourceApp?: ResourceApp
}

const APP_ICON_SIZE = 16
const APP_ICON_CLASS = 'tw:flex-none'

@observer
export default class ProjectTypeSetting extends React.Component<ProjectTypeSettingProps> {
  public render() {
    return (
      <div className="tw:flex tw:w-full tw:justify-center tw:py-6">
        <div className="tw:w-[608px] tw:max-w-full">
          <SettingsFormSection title={Localization.translator.manageApps}>
            <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
              <AppTogglesHeader />
              {this.renderRows()}
            </div>
          </SettingsFormSection>
        </div>
      </div>
    )
  }

  // Each top-level app is its own group; the Resources row and its apps share one.
  // Groups are divided by a separator, rows within a group are not.
  private renderRows(): JSX.Element[] {
    const configs = this.appConfigs.filter(config => config.isAvailable)
    const resourceConfigs = configs.filter(config => config.resourceApp)
    const groups: JSX.Element[][] = []

    configs.forEach(config => {
      if (!config.resourceApp) {
        groups.push([this.renderAppToggle(config)])
        return
      }
      if (config === resourceConfigs[0]) {
        groups.push([this.renderResourcesRow(resourceConfigs)])
      }
      groups[groups.length - 1].push(this.renderAppToggle(config))
    })

    return groups.flatMap((rows, index) =>
      index === 0
        ? rows
        : [
            <Separator
              key={`separator-${index}`}
              className="tw:bg-input-soft"
            />,
            ...rows,
          ],
    )
  }

  private renderResourcesRow(resourceConfigs: AppConfig[]): JSX.Element {
    const { store } = this.props
    const enabledResourcesCount = resourceConfigs.filter(
      config => config.isEnabled,
    ).length
    const isAnyEnabled = enabledResourcesCount > 0

    return (
      <AppToggle
        key="resources"
        isAvailable={true}
        label={Localization.translator.resources}
        icon={<ComponentIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />}
        isEnabled={isAnyEnabled}
        onToggle={() =>
          store.setResourceAppsEnabled(
            !isAnyEnabled,
            resourceConfigs.map(config => config.resourceApp),
          )
        }
        isDisabledToggle={
          isAnyEnabled && this.getActiveAppsCount === enabledResourcesCount
        }
      />
    )
  }

  private renderAppToggle(config: AppConfig): JSX.Element {
    const { store } = this.props
    const hasOnlyOneActiveApp = this.getActiveAppsCount === 1

    return (
      <AppToggle
        key={config.landingPage}
        isAvailable={config.isAvailable}
        label={config.label}
        isEnabled={config.isEnabled}
        onToggle={config.onToggle}
        icon={config.icon}
        shouldRenderCheckbox={true}
        isChecked={store.defaultLandingPage === config.landingPage}
        onCheckboxToggle={() =>
          store.setProjectDefaultLandingPage(config.landingPage)
        }
        isDisabledToggle={hasOnlyOneActiveApp && config.isActiveApp}
        isNested={!!config.resourceApp}
      />
    )
  }

  private get appConfigs(): AppConfig[] {
    const {
      store,
      isAnalyticsAvailable,
      isLogisticsToggleAvailable,
      isDeliveriesToggleAvailable,
      isFormsToggleAvailable,
      isMaterialsToggleAvailable,
      isMaterialResourcesToggleAvailable,
      isPhotosAvailable,
      isEquipmentToggleAvailable,
      isStruxTagsAvailable,
      isLocationsToggleAvailable,
    } = this.props

    return [
      {
        isAvailable: isLogisticsToggleAvailable,
        label: Localization.translator.home,
        isEnabled: store.isLogisticsEnabled,
        onToggle: store.toggleLogisticsEnabled,
        icon: <HouseIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />,
        landingPage: DefaultLandingPage.Home,
        isActiveApp: store.isLogisticsEnabled,
      },
      {
        isAvailable: isFormsToggleAvailable,
        label: Localization.translator.workflows,
        isEnabled: store.isFormsEnabled,
        onToggle: store.toggleFormsEnabled,
        icon: (
          <GenericWorkflowIcon
            size={APP_ICON_SIZE}
            className={APP_ICON_CLASS}
          />
        ),
        landingPage: DefaultLandingPage.Forms,
        isActiveApp: store.isFormsEnabled,
      },
      {
        isAvailable: isLocationsToggleAvailable,
        label: Localization.translator.locations,
        isEnabled: store.isLocationsEnabled,
        onToggle: store.toggleLocationsEnabled,
        icon: <MapPinIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />,
        landingPage: DefaultLandingPage.Locations,
        isActiveApp: store.isLocationsEnabled,
        resourceApp: 'locations',
      },
      {
        isAvailable: isEquipmentToggleAvailable,
        label: Localization.translator.equipment,
        isEnabled: store.isEquipmentEnabled,
        onToggle: store.toggleEquipmentEnabled,
        icon: <WrenchIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />,
        landingPage: DefaultLandingPage.Equipment,
        isActiveApp: store.isEquipmentEnabled,
        resourceApp: 'equipment',
      },
      {
        isAvailable: isMaterialResourcesToggleAvailable,
        label: Localization.translator.materials,
        isEnabled: store.isMaterialResourcesEnabled,
        onToggle: store.toggleMaterialResourcesEnabled,
        icon: <PackageIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />,
        landingPage: DefaultLandingPage.MaterialResources,
        isActiveApp: store.isMaterialResourcesEnabled,
        resourceApp: 'materialResources',
      },
      {
        isAvailable: isStruxTagsAvailable,
        label: Localization.translator.struxTags,
        isEnabled: store.isStruxTagEnabled,
        onToggle: store.toggleStruxTagEnabled,
        icon: <RadioIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />,
        landingPage: DefaultLandingPage.StruxTags,
        isActiveApp: store.isStruxTagEnabled,
        resourceApp: 'struxTags',
      },
      {
        isAvailable: isPhotosAvailable,
        label: Localization.translator.files,
        isEnabled: store.isPhotosEnabled,
        onToggle: store.togglePhotosEnabled,
        icon: <PaperclipIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />,
        landingPage: DefaultLandingPage.Photos,
        isActiveApp: store.isPhotosEnabled,
      },
      {
        isAvailable: isAnalyticsAvailable,
        label: Localization.translator.analytics,
        isEnabled: store.isAnalyticsEnabled,
        onToggle: store.toggleAnalyticsEnabled,
        icon: (
          <ChartColumnIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />
        ),
        landingPage: DefaultLandingPage.Analytics,
        isActiveApp: store.isAnalyticsEnabled,
      },
      {
        isAvailable: isDeliveriesToggleAvailable,
        label: Localization.translator.deliveries,
        isEnabled: store.isDeliveriesEnabled,
        onToggle: store.toggleDeliveriesEnabled,
        icon: <TruckIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />,
        landingPage: DefaultLandingPage.Deliveries,
        isActiveApp: store.isDeliveriesEnabled,
      },
      {
        isAvailable: isMaterialsToggleAvailable,
        label: Localization.translator.materialsLegacy,
        isEnabled: store.isMaterialsEnabled,
        onToggle: store.toggleMaterialsEnabled,
        icon: <BoxIcon size={APP_ICON_SIZE} className={APP_ICON_CLASS} />,
        landingPage: DefaultLandingPage.Materials,
        isActiveApp: store.isMaterialsEnabled,
      },
    ] as AppConfig[]
  }

  private get getActiveAppsCount(): number {
    const { store } = this.props
    return [
      store.isDeliveriesEnabled,
      store.isAnalyticsEnabled,
      store.isMaterialsEnabled,
      store.isMaterialResourcesEnabled,
      store.isPhotosEnabled,
      store.isLogisticsEnabled,
      store.isFormsEnabled,
      store.isEquipmentEnabled,
      store.isStruxTagEnabled,
      store.isLocationsEnabled,
    ].filter(Boolean).length
  }
}
